import {Button, Col, Row, Space} from "antd";
import {FormProvider, useForm} from "react-hook-form";
import {KycLegalNameFormField} from "./form-fields/legal-name";
import {KycBusinessNameFormField} from "./form-fields/business-name";
import {
  createKycBasicFormStepFormValidationSchema,
  CreateKycBasicFormStepFormValues,
} from "./schema-validation";
import {yupResolver} from "@hookform/resolvers/yup";
import {KycBusinessDescriptionFormField} from "./form-fields/business-description";
import {KycEmailFormField} from "./form-fields/email";
import {
  KycDateOfBirthFormField,
  parseFromBackend,
  serialiseToBackend,
} from "./form-fields/date-of-birth";
import {KycGenderFormField} from "./form-fields/gender";
import {KycCountryFormField} from "./form-fields/country";
import {KycResidentialAddressFormField} from "./form-fields/residential-address";
import {KycPostalCodeFormField} from "./form-fields/postal-code";
import {KycPhoneNumberFormField} from "./form-fields/phone-number";
import {KycSocialLinksFormField, KycSocialMediaOptions} from "./form-fields/social-links";
import {useGetKycStatusContext} from "../../../get-kyc-status/context/get-kyc-status";
import {useCreateKycRecordHook} from "../../hooks/create-kyc-record";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {useCreateKycContext} from "../../context/create-kyc";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {parseE164} from "../../../../../shared/phone";

/** Convert whatever the API sent into { value, error } for defaultValues */
function normaliseForForm(raw: string | undefined): {
  value: string | undefined;
  error: string | null;
} {
  const m = parseFromBackend(raw);
  if (m) {
    const sb = serialiseToBackend(m);
    return {value: sb ?? undefined, error: null};
  }
  if (!raw) return {value: undefined, error: null}; // field allowed to be empty
  return {value: undefined, error: "Date received from server is invalid"};
}

export function CreateKycBasicDetailsStep() {
  const {state: kycStatusState} = useGetKycStatusContext();
  const existingKycId = kycStatusState.kycStatusData?.data.id;
  const {createKycRecord, isCreatingKycRecord} = useCreateKycRecordHook(existingKycId);
  const {state: profileState} = useGetProfileContext();
  const {state: createKycState} = useCreateKycContext();

  // Check if user has entered data in context (navigated back from step 2)
  const hasContextData = createKycState.basicFormData.legalName.trim().length > 0;

  /* Normalise once, before RHF mounts */
  const {value: initialDob} = normaliseForForm(
    kycStatusState.kycStatusData?.data.dateOfBirth
  );

  const {numberCode: initialNumberCode, phoneNumber: initialPhoneNumber} = parseE164(
    kycStatusState.kycStatusData?.data.phoneNumber
  );

  const formMethods = useForm<CreateKycBasicFormStepFormValues>({
    resolver: yupResolver(createKycBasicFormStepFormValidationSchema),
    mode: "onChange",
    defaultValues: hasContextData
      ? {
          // User navigated back from step 2 - use context data to preserve their entries
          address: createKycState.basicFormData.address,
          businessDescription: createKycState.basicFormData.businessDescription,
          businessName: createKycState.basicFormData.businessName,
          country: createKycState.basicFormData.country,
          dateOfBirth: createKycState.basicFormData.dateOfBirth,
          emailAddress: profileState.profileData.email.toLowerCase() || "",
          gender: createKycState.basicFormData.gender,
          legalName: createKycState.basicFormData.legalName,
          postalCode: createKycState.basicFormData.postalCode,
          numberCode: createKycState.basicFormData.numberCode,
          phoneNumber: createKycState.basicFormData.phoneNumber,
          socialLinks: createKycState.basicFormData.socialLinks,
        }
      : {
          // Initial load - use server data
          address: kycStatusState.kycStatusData?.data.address || "",
          businessDescription:
            kycStatusState.kycStatusData?.data.serviceDescription || "",
          businessName: kycStatusState.kycStatusData?.data.businessName || "",
          country: kycStatusState.kycStatusData?.data.country || "",
          dateOfBirth: initialDob,
          emailAddress:
            kycStatusState.kycStatusData?.data.email ||
            profileState.profileData.email.toLowerCase() ||
            "",
          gender: kycStatusState.kycStatusData?.data.gender || "",
          legalName: kycStatusState.kycStatusData?.data.fullName || "",
          postalCode: kycStatusState.kycStatusData?.data.postalCode || "",
          numberCode: initialNumberCode,
          phoneNumber: initialPhoneNumber,
          socialLinks: (() => {
            const raw = kycStatusState.kycStatusData?.data.socialLinks;
            if (!raw) return [];
            if (Array.isArray(raw)) {
              // V1: string[] — no platform name available, drop into a generic entry
              return raw
                .filter((link) => !!link)
                .map((link) => ({name: "LinkedIn" as KycSocialMediaOptions, link}));
            }
            // V2: { [platform]: url }
            return Object.entries(raw)
              .filter(([_, link]) => !!link)
              .map(([name, link]) => ({
                name: name as KycSocialMediaOptions,
                link: link as string,
              }));
          })(),
        },
  });

  function onSubmit(values: CreateKycBasicFormStepFormValues) {
    // console.log({values});
    createKycRecord({
      ...values,
      emailAddress: profileState.profileData.email.toLowerCase(),
    });
  }
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
        <Space direction="vertical" size={24} style={{display: "flex"}}>
          <KycLegalNameFormField />
          <KycBusinessNameFormField />
          <KycBusinessDescriptionFormField />
          <KycSocialLinksFormField />
          {/* <KycEmailFormField /> */}
          <KycDateOfBirthFormField />
          <KycGenderFormField />
          <KycCountryFormField />
          <KycResidentialAddressFormField />
          <KycPostalCodeFormField />
          <KycPhoneNumberFormField />

          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  // block
                  type="primary"
                  htmlType={"submit"}
                  size="small"
                  disabled={
                    Object.keys(formMethods.formState.errors).length > 0 ||
                    Object.values(formMethods.watch()).length < 1
                  }
                  loading={isCreatingKycRecord}
                >
                  Next
                </Button>
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
    </FormProvider>
  );
}
